// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  input,
  ViewChild,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ChartsService } from '../services/charts-data.service';

@Component({
  standalone: true,
  imports: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-amount-consumed-graph',
  templateUrl: './amount-consumed-graph.component.html',
  styleUrl: './amount-consumed-graph.component.scss',
})
export class AmountConsumedGraphComponent implements AfterViewInit {
  values = input<any[]>([]);

  @ViewChild('chart') chartViewRef: ElementRef<HTMLDivElement> | undefined;

  private readonly graphLabels = {
    title: this.translateService.instant('spareParts.AmountConsumed.title'),
    subtitle: this.translateService.instant('spareParts.subtitle.last12Months'),
    xAxisName: this.translateService.instant(
      'spareParts.AmountConsumed.xAxisName',
    ),
    yAxisName: this.translateService.instant(
      'spareParts.AmountConsumed.yAxisName',
    ),
    months: this.translateService.instant(
      'spareParts.AmountConsumed.months',
    ) as Array<string>,
  };
  private readonly now = new Date();
  private readonly endRange = new Date(
    this.now.getFullYear(),
    this.now.getMonth(),
    -1,
  );

  private readonly startRange = new Date(
    this.endRange.getFullYear(),
    this.endRange.getMonth() - 11,
    1,
  );

  private readonly labels = this.generateLabels(this.startRange, this.endRange);

  private readonly defaultColor = '#7D7D7D';
  private readonly highlightColor = '#EC6602';
  private readonly siemensSans = 'siemens sans';

  public constructor(
    private readonly translateService: TranslateService,
    private readonly chartService: ChartsService,
  ) {}

  public ngAfterViewInit(): void {
    const chartEl = this.chartViewRef?.nativeElement;
    const option = this.chartService.buildDefaultOptions(
      this.chartService.buildTitle(
        this.graphLabels.title,
        this.graphLabels.subtitle,
      ),
      this.buildXAxis(),
      this.buildYAxis(),
      this.buildSeries(),
      {
        right: 0,
        left: 50,
        top: 50,
        bottom: 50,
      },
    );
    if (chartEl) {
      this.chartService.buildChart(chartEl, option);
    }
  }

  public generateLabels(start: Date, end: Date): string[] {
    const monthNames = this.graphLabels.months;
    const labels: string[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);

    while (current <= end) {
      const monthLabel = monthNames[current.getMonth()];
      labels.push(monthLabel);

      if (current.getMonth() === 11) {
        labels.push((current.getFullYear() + 1).toString());
      }

      current.setMonth(current.getMonth() + 1);
    }
    return labels;
  }

  private buildXAxis(): { [key: string]: any } {
    return {
      type: 'category',
      data: this.labels.map((month) => {
        const isYear = !this.graphLabels.months.includes(month);
        return {
          value: month,
          textStyle: {
            fontFamily: this.siemensSans,
            fontWeight: isYear ? 600 : 400,
            fontSize: isYear ? 14 : 12,
            color: isYear ? '#000' : this.defaultColor,
          },
        };
      }),
      name: this.graphLabels.xAxisName,
      nameLocation: 'middle',
      nameTextStyle: {
        color: 'rgba(0, 0, 0, 0.90)',
        fontSize: 14,
        fontWeight: 700,
        fontFamily: this.siemensSans,
      },
      nameGap: 30,
    };
  }

  private buildYAxis(): { [key: string]: any } {
    return {
      type: 'value',
      minInterval: 1,
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
        },
      },
      name: this.graphLabels.yAxisName,
      nameLocation: 'center',
      nameTextStyle: {
        color: 'rgba(0, 0, 0, 0.90)',
        fontSize: 14,
        fontWeight: 700,
        textMargin: '20px',
        fontFamily: this.siemensSans,
      },
      nameGap: 30,
    };
  }

  private buildSeries(): { [key: string]: any }[] {
    const labels = this.labels;
    const dataChart = this.values();

    const quantities = dataChart.map((item) => item.quantity);
    const maxValue = quantities.length ? Math.max(...quantities) : 0;

    const data = labels.map((label) => {
      const isYear = !this.graphLabels.months.includes(label);

      if (isYear) {
        return { value: null };
      }
      const itemToBar = dataChart.find(
        (item) => item && this.graphLabels.months[item.month - 1] === label,
      );

      if (!itemToBar) {
        return { value: null };
      }

      const v = itemToBar.quantity;
      return v === maxValue
        ? { value: v, itemStyle: { color: this.highlightColor } }
        : { value: v, itemStyle: { color: this.defaultColor } };
    });

    return [
      {
        type: 'bar',
        data,
      },
    ];
  }
}
